import { Component } from '@theme/component';

/**
 * A custom element that displays and updates a live viewer count.
 *
 * @typedef {object} LiveViewerCountRefs
 * @property {HTMLElement} viewerCount - The element displaying the viewer count
 * @extends Component<LiveViewerCountRefs>
 */
class LiveViewerCountComponent extends Component {
  requiredRefs = ['viewerCount'];

  /** @type {number | null} */
  #updateInterval = null;

  /** @type {number} */
  #currentCount = 0;

  connectedCallback() {
    super.connectedCallback();

    const minViewers = parseInt(this.dataset.minViewers || '1', 10);
    const maxViewers = parseInt(this.dataset.maxViewers || '50', 10);
    const updateInterval = parseInt(this.dataset.updateInterval || '30', 10) * 1000;

    // Set initial count
    this.#currentCount = this.#generateViewerCount(minViewers, maxViewers);
    this.#updateDisplay();

    // Add pulse animation
    this.setAttribute('data-pulse', 'true');

    // Update count at intervals
    this.#updateInterval = setInterval(() => {
      this.#currentCount = this.#generateViewerCount(minViewers, maxViewers);
      this.#updateDisplay();
    }, updateInterval);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateInterval) {
      clearInterval(this.#updateInterval);
      this.#updateInterval = null;
    }
  }

  /**
   * Generates a random viewer count within the specified range
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #generateViewerCount(min, max) {
    // Generate a count that's within ±20% of current count for realism
    const variance = Math.floor(this.#currentCount * 0.2) || 5;
    const newMin = Math.max(min, this.#currentCount - variance);
    const newMax = Math.min(max, this.#currentCount + variance);
    
    return Math.floor(Math.random() * (newMax - newMin + 1)) + newMin;
  }

  /**
   * Updates the display with the current count
   */
  #updateDisplay() {
    if (this.refs.viewerCount) {
      // Animate the number change
      this.refs.viewerCount.style.opacity = '0.5';
      setTimeout(() => {
        if (this.refs.viewerCount) {
          this.refs.viewerCount.textContent = this.#currentCount.toString();
          this.refs.viewerCount.style.opacity = '1';
        }
      }, 150);
    }
  }
}

if (!customElements.get('live-viewer-count-component')) {
  customElements.define('live-viewer-count-component', LiveViewerCountComponent);
}

